import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import Select from 'core/components/Select';
import {
  ClaimMeasurementUnitCodeSettingDTO,
  NdcCodeDTO,
} from 'dtos';
import {
  useClaimSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import Button from 'pages/Dashboard/components/Button';
import {
  isEmptyString,
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import EditIcon from 'core/icons/edit';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Popover from 'core/components/Popover';
import NumberTextField from 'core/components/NumberTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  serviceLookupEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import keyBy from 'lodash/keyBy';
import ToolTip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  getApiClaimSearchNdcCodes,
} from 'endpoints';
import PopulateLookup from 'core/components/Lookup';

type Props = {
  data: VisitNoteServiceCode;
  index?: number;
};

type FormData = {
  ndc?: string | null;
  unitCount?: number | null;
  unitCode?: ClaimMeasurementUnitCodeSettingDTO;
};

type DrugEditorProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  index: number;
  data: VisitNoteServiceCode;
};

type RowProps = {
  item?: string | null;
  className?: string;
};

function Row({ item, className }: RowProps) {
  return (<span key={item} className={clsx('text-xs !font-normal', className)}>{item ?? '--'}</span>);
}

function DrugForm() {
  const { control } = useFormContext<FormData>();
  const { measurementUnitCodeSettings } = useClaimSettings();

  return (
    <div className="flex gap-2 populate-form items-start populate-form-sm">
      <Controller
        control={control}
        name="ndc"
        render={({ field: { onChange, value, ...field } }) => (
          <Wrapper
            className="flex-1"
            label="NDC"
            id="ndc"
            showErrorText={false}
          >
            <PopulateLookup<NdcCodeDTO>
              value={{
                convertedNdcPackageCode: value,
              } as NdcCodeDTO}
              onChange={(newValue: NdcCodeDTO | NdcCodeDTO[]) => {
                onChange((newValue as NdcCodeDTO)?.convertedNdcPackageCode);
              }}
              placeholder="Search NDC codes"
              getOptions={getApiClaimSearchNdcCodes}
              multiple={false}
              classes={{ dropdownContainer: '!w-[330px]' }}
              isOptionEqualToValue={
                (
                  option,
                  value,
                ) => option?.convertedNdcPackageCode === (
                  value as NdcCodeDTO
                )?.convertedNdcPackageCode
              }
              getUniqueId={(value) => value?.convertedNdcPackageCode?.toLowerCase() ?? ''}
              getLabel={(value) => value?.convertedNdcPackageCode ?? ''}
              renderOptionRow={(option) => (
                <div className="flex flex-row">
                  <ToolTip
                    popperClassName="billing-code-content"
                    placement="right"
                    title={(
                      <p className="flex flex-col gap-1">
                        <span className="block text-xs font-medium">
                          {option?.convertedNdcPackageCode ?? '--'}
                        </span>
                        <Row item={option?.proprietaryName ?? '--'} />
                        <Row item={option?.nonProprietaryName ?? '--'} />
                        <Row item={option?.activeIngredientUnit ?? '--'} />
                        <Row item={option?.packageDescription ?? '--'} />
                      </p>
                    )}
                  >
                    <div className="flex flex-col !items-start !justify-start !w-full !text-left gap-2">
                      <span className="font-semibold">
                        {option?.convertedNdcPackageCode ?? '--'}
                      </span>
                      <span className="flex flex-col items-start gap-1 !text-xs">
                        <Row item={option?.proprietaryName ?? '--'} />
                        <Row item={option?.nonProprietaryName ?? '--'} />
                        <Row item={option?.activeIngredientUnit ?? '--'} />
                        <Row item={option?.packageDescription ?? '--'} />
                      </span>
                    </div>
                  </ToolTip>
                </div>
              )}
            />
          </Wrapper>
        )}
      />
      <div className="flex gap-2 items-end flex-1">
        <Controller
          control={control}
          name="unitCount"
          render={({ field }) => (
            <Wrapper
              className="w-full"
              label="Unit"
              id="unit-count"
              showErrorText={false}
            >
              <NumberTextField
                {...field}
                maskExtension={{ min: 0 }}
              />
            </Wrapper>
          )}
        />
        <Controller
          control={control}
          name="unitCode"
          render={({ field }) => (
            <Select<ClaimMeasurementUnitCodeSettingDTO>
              {...field}
              id="drug-unit-code"
              className="w-full max-w-[240px] text-gray-500 text-xs"
              placeholder="Select"
              options={measurementUnitCodeSettings ?? []}
              getLabel={(option) => (
                `${option?.code} - ${option?.description ?? ''}`
              )}
              getValue={(option) => option?.code ?? ''}
              renderValue={() => field?.value?.code}
            />
          )}
        />
      </div>
    </div>
  );
}

function DrugEditor({
  anchorEl,
  onClose,
  index,
  data,
}: DrugEditorProps) {
  const {
    ndc,
    unitCount,
    unitCode,
  } = data?.serviceCodeDetails?.drugIdentification ?? {};

  const defaultValues = data?.serviceCodeDefaults?.drugIdentification;

  const formProps = useForm<FormData>({
    defaultValues: {
      ndc: ndc ?? defaultValues?.ndc ?? '',
      unitCount: unitCount ?? defaultValues?.unitCount,
      unitCode: unitCode ?? { code: defaultValues?.unitCode?.code ?? '' },
    },
  });

  const { reset, handleSubmit } = formProps;

  const handleClose = () => {
    onClose();
    reset({
      ndc: ndc ?? '',
      unitCount: unitCount ?? 0,
      unitCode: unitCode ?? {},
    });
  };

  const onSubmit = ({ ndc, unitCount, unitCode }: FormData) => {
    serviceLookupEventChannel.emit('onServiceCodeLineUpdate', {
      index: index ?? 0,
      value: { ndc, unitCount, unitCode },
      id: 'drugIdentification',
      data,
    });
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={handleClose}
      classes={{ paper: '!w-[420px] ' }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <FormProvider {...formProps}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4 flex flex-col gap-2">
            <DrugForm />
            <div className="flex justify-between items-center gap-2">
              <Button
                variant="outlined"
                type="button"
                className="flex-1 !max-w-[unset]"
                onClick={() => handleClose()}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className="flex-1 !max-w-[unset]"
                color="primary"
                type="submit"
                onClick={handleSubmit(onSubmit)}
              >
                Confirm
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Popover>
  );
}

function DrugCell({
  data,
  index,
}: Props) {
  const {
    ndc,
    unitCount,
    unitCode,
  } = data?.serviceCodeDetails?.drugIdentification ?? {};
  const empty = isEmptyString(ndc) && isNilOrZero(unitCount);

  const { measurementUnitCodeSettings } = useClaimSettings();

  const unitDetailsByCode = useMemo(
    () => keyBy(measurementUnitCodeSettings, 'code'),
    [measurementUnitCodeSettings],
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const drugUnit = useMemo(() => {
    const unit = `${unitCount} ${unitDetailsByCode[unitCode?.code as string]?.abbrevation ?? ''}`;
    return notEmptyString(ndc)
      ? `(${unit})`
      : unit;
  }, [unitCount, unitCode]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex items-center gap-1 w-full text-xs">
      <Button
        variant="outlined"
        color="primary"
        className={clsx('flex !px-1', {
          '!font-medium !border-dashed !py-0 !rounded-full !w-5 !h-5 !min-w-[initial] !flex !items-center !border-blue-500': empty,
          '!text-black !min-w-[120px] gap-2 !justify-between !py-0.5 !w-[200px] !max-w-[initial]': !empty,
        })}
        onClick={(e) => {
          e.stopPropagation();
          buttonRef?.current?.focus();
          setAnchorEl(e.currentTarget?.parentElement);
        }}
        ref={buttonRef}
      >
        {empty ? (
          <AddIcon className="!w-4 !h-4 !text-inherit" />
        ) : (
          <>
            <div className="flex items-start text-[11px] truncate gap-1 text-left font-medium">
              {notEmptyString(ndc) && (
              <div className="truncate">
                {ndc}
              </div>
              )}
              {!isNilOrZero(unitCount) && (
              <div className="text-gray-600">
                {drugUnit}
              </div>
              )}
            </div>
            {!empty && (<EditIcon className="!w-4 !h-4 !text-gray-800" />)}
          </>
        )}
      </Button>
      {!empty && (
        <ToolTip title="Remove Drug">
          <IconButton
            className="!p-0"
            onClick={() => {
              serviceLookupEventChannel.emit('onServiceCodeLineUpdate', {
                index: index ?? 0,
                value: {
                  ndc: null,
                  unitCount: null,
                  unitCode: undefined,
                },
                id: 'drugIdentification',
                data,
              });
            }}
          >
            <CloseIcon className="!w-4 !h-4" />
          </IconButton>
        </ToolTip>
      )}
      {!isNil(anchorEl) && (
        <DrugEditor
          anchorEl={anchorEl}
          onClose={handleClose}
          index={index ?? 0}
          data={data}
        />
      )}
    </div>
  );
}

export default {
  id: 'drugIdentification',
  columnTitle: 'Drug',
  multiLineHeader: true,
  size: 200,
  minSize: 200,
  cell: ({ row }) => <DrugCell data={row.original} index={row.index} />,
} as ColumnDef<VisitNoteServiceCode>;
